import { Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PRICING, COMMERCIAL_SITE } from '@connexio/shared/constants';
import { useLocale } from '@/hooks/useLocale';
import { formatNumber } from '@/lib/formatters';
import { commercialOnSitePath } from '@/lib/commercialContact';
import { MARKETING_INLINE_LINK_CLASS, Section, SectionHeading } from './Section';
import styles from './Faq.module.css';
import { cn } from '@/lib/utils';

/** `afterTrial` is only shown while self-service sign-up is open. */
const FAQ_KEYS = ['price', 'import', 'afterTrial', 'safe', 'data', 'cancel', 'support'] as const;

/** Answers about data handling end with a link to the full privacy policy. */
const PRIVACY_LINKED_KEYS: ReadonlySet<(typeof FAQ_KEYS)[number]> = new Set(['safe', 'data']);

function PrivacyLink() {
  const { t } = useTranslation('marketing');
  const href = COMMERCIAL_SITE.privacyUrl;
  if (!href) return null;
  const onSite = commercialOnSitePath(href);
  return onSite ? (
    <Link to={onSite} className={MARKETING_INLINE_LINK_CLASS}>
      {t('faq.privacyLink')}
    </Link>
  ) : (
    <a href={href} className={MARKETING_INLINE_LINK_CLASS}>
      {t('faq.privacyLink')}
    </a>
  );
}

export function Faq({ signupEnabled }: { signupEnabled: boolean }) {
  const { t } = useTranslation('marketing');
  const { locale } = useLocale();
  const keys = FAQ_KEYS.filter((key) => signupEnabled || key !== 'afterTrial');
  const values = {
    days: COMMERCIAL_PRICING.trialDays,
    exVat: formatNumber(COMMERCIAL_PRICING.amountExVatSek, locale),
    incVat: formatNumber(COMMERCIAL_PRICING.amountIncVatSek, locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
    vat: COMMERCIAL_PRICING.vatRatePercent,
  };

  return (
    <Section id="faq" tone="tint" labelledBy="faq-title">
      <div className={styles.grid}>
        <SectionHeading id="faq-title" eyebrow={t('faq.eyebrow')} title={t('faq.title')} />

        <div className={styles.box}>
          {keys.map((key) => (
            <details key={key} className={cn(styles.details, 'group')}>
              <summary className={styles.summary}>
                <h3 className={styles.heading}>
                  {t(`faq.items.${key}.q`, values)}
                </h3>
                <Plus
                  className={styles.icon}
                  aria-hidden="true"
                />
              </summary>
              <div className={styles.box2}>
                <p>{t(`faq.items.${key}.a`, values)}</p>
                {PRIVACY_LINKED_KEYS.has(key) ? (
                  <p className={styles.text}>
                    <PrivacyLink />
                  </p>
                ) : null}
              </div>
            </details>
          ))}
        </div>
      </div>
    </Section>
  );
}
